/**
 * IA-side branding fetch (Phase H0 hotfix).
 *
 * The IA portal previously hard-coded `defaultPalette="ocean-breeze"`
 * and a generic `<SidebarBrand title="EduViaOS" />` wordmark, which
 * meant a tenant who customised their colour and logo via the SA
 * branding editor saw NONE of it inside their own admin portal —
 * branding only showed up on the public apply portal.
 *
 * `useTenantBranding()` reads the public branding endpoint
 * (anonymous; the IA SPA already sets the `X-Tenant-Slug` header on
 * every request) and feeds the result to `<TenantBrandingProvider>`.
 *
 * Read-only on this side — the SA owns commit. We deliberately use
 * a long `staleTime` because branding changes are rare and the BE
 * already serves `Cache-Control: private, max-age=0, must-revalidate`
 * (Phase H0 BE) so a hard-refresh re-validates instantly.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentTenant } from "@eduviaos/shared";
import { api } from "@/lib/api";
import type {
  AssetKind,
  BrandingPublic,
  CommitBrandingRequest,
  UploadUrlRequest,
  UploadUrlResponse,
} from "./types";

const PUBLIC_BASE = (slug: string) => `/tenants/${slug}/branding`;
// Phase F2b — tenant-scoped self-serve write surface. No slug in the
// path; the BE resolves the tenant from the authed admin's JWT.
const ME_BASE = "/me/branding";

export function useTenantBranding(slug: string | null | undefined) {
  return useQuery<BrandingPublic>({
    queryKey: ["branding", slug],
    queryFn: () => api.get<BrandingPublic>(PUBLIC_BASE(slug as string)),
    enabled: Boolean(slug),
    // Branding mutations are infrequent; a 5-minute stale window
    // avoids a refetch on every page nav. The cache-header policy
    // change in Phase H0 BE guarantees the next mount fetch lands
    // fresh after a Save.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Phase F2b — IA self-serve branding writes.
 *
 * The commit / delete hooks prime the same `["branding", slug]` cache
 * key that `<TenantBrandingProvider>` reads, so saving repaints the
 * live CSS variables (primary / secondary) and swaps the sidebar logo
 * without a full reload. The slug is resolved from the host (identical
 * to how `App.tsx` seeds the provider) purely as a cache key — the BE
 * write itself is keyed on the JWT, never this value.
 */
function currentSlug(): string | null {
  return getCurrentTenant().slug ?? null;
}

export function useMintUploadUrl(kind: AssetKind) {
  return useMutation<UploadUrlResponse, Error, UploadUrlRequest>({
    mutationFn: (body) => api.post<UploadUrlResponse>(`${ME_BASE}/${kind}/upload-url`, body),
  });
}

export function useCommitBranding() {
  const qc = useQueryClient();
  return useMutation<BrandingPublic, Error, CommitBrandingRequest>({
    mutationFn: (body) => api.put<BrandingPublic>(ME_BASE, body),
    onSuccess: (returned) => {
      const slug = currentSlug();
      if (slug) {
        qc.setQueryData(["branding", slug], returned);
        qc.invalidateQueries({ queryKey: ["branding", slug] });
      }
    },
  });
}

export function useDeleteBrandingAsset() {
  const qc = useQueryClient();
  return useMutation<BrandingPublic | void, Error, AssetKind>({
    mutationFn: async (kind) => {
      await api.delete(`${ME_BASE}/${kind}`);
      const slug = currentSlug();
      return slug ? api.get<BrandingPublic>(PUBLIC_BASE(slug)) : undefined;
    },
    onSuccess: (refreshed) => {
      const slug = currentSlug();
      if (slug && refreshed) {
        qc.setQueryData(["branding", slug], refreshed);
      }
      if (slug) {
        qc.invalidateQueries({ queryKey: ["branding", slug] });
      }
    },
  });
}
